import React, {useEffect, useLayoutEffect, useMemo, useRef, useState} from "react";
import {Box, Tooltip} from "@mui/material";
import useIsMounted from "../../../../../../../hooks/use-is-mounted";
import ObserversService from "../../../../../../../../core/services/observers";
import {routeFunctions} from "../../../../../../../../core/models/static/routes/app-routes";
import Slider from "react-slick";
import LazyImage from "../../../../../../../components/app-specific/lazy-image";
import classnames from "classnames";
import {Fade} from "reactstrap";
import {ReactComponent as RemoveIcon} from "../../../../../../../../assets/images/remove.svg";
import {ReactComponent as EyeIcon} from "../../../../../../../../assets/images/password-icons/eye.svg";
import {ReactComponent as PinIcon} from "../../../../../../../../assets/images/comparison/pin.svg";
import {ReactComponent as PinnedIcon} from "../../../../../../../../assets/images/comparison/pinned.svg";
import {Close} from "@mui/icons-material";
import IconButton from "../../../../../../../components/base/button/icon_button";
import Utils from "../../../../../../../../core/services/utils/utils";
import {Link} from "react-router-dom";
import {ProductTechnicalPropertyTypeIds} from "../../../../../../../../core/constants/enums";

const CompareViewTableSection = ({selectedCategory, parts, togglePinned, removePart}) => {
    const [removedPropertyIds, setRemovedPropertyIds] = useState([]);
    const [infoMaxHeight, setInfoMaxHeight] = useState(200);

    const pinnedParts = useMemo(() => parts?.filter(e => e.pinned) ?? [], [parts])
    const unpinnedParts = useMemo(() => parts?.filter(e => !e.pinned) ?? [], [parts])

    const properties = useMemo(() => {
        const technicalProperties = [];
        for (const part of parts) {
            for (const property of part.properties) {
                if (!technicalProperties.some(e => e.id === property.propertyId) && !!property.propertyId) {
                    technicalProperties.push({
                        id: property.propertyId,
                        title: property.propertyTitle,
                        industryPropertyTypeId: property.industryPropertyTypeId,
                    })
                }
            }
        }
        const propsWithProducts = {};
        for (const property of technicalProperties) {
            propsWithProducts[property.id] = {
                ...property,
                parts: parts.filter(e => e.properties.some(p => p.propertyId === property.id))
            }
        }
        const properties = Object.values(propsWithProducts);
        return properties
            .filter(e => e.parts.length > 0)
            .filter(e => !removedPropertyIds.includes(e.id))
            .sort((a, b) => b.parts.length - a.parts.length);
    }, [parts, removedPropertyIds])

    /**
     * With each change in the selected category:
     * - sets the technical properties of the state.
     * - resets the max height of the information section
     */
    useLayoutEffect(() => {
        setInfoMaxHeight(200);
        setRemovedPropertyIds([]);
    }, [selectedCategory?.id])

    /**
     * Removes the given property from the list of technical properties of the state.
     * @param property
     */
    const removeProperty = (property) => {
        setRemovedPropertyIds(prevState => [...prevState, property.id]);
    }

    /**
     * Sets the max height of the information section.
     * @param {number} height
     */
    const setInformationMaxHeight = (height) => {
        setInfoMaxHeight(prevState => Math.max(height, prevState));
    }


    return (
        <>
            <div
                className={'compare-table-wrapper'}
                style={{
                    '--info-height': `${infoMaxHeight}px`,
                }}
            >
                <table className={'pinned-compare-table'}>
                    <thead>
                    <CompareViewTableHeaderRow
                        parts={pinnedParts}
                        togglePinned={togglePinned}
                        pinned
                    />
                    </thead>
                    <tbody>
                    {
                        [null, ...(properties ?? [])].map(property => (
                            <CompareViewTableBodyRow
                                key={`table-row-${property?.id ?? 'init'}`}
                                property={property}
                                parts={pinnedParts}
                                removeProperty={removeProperty}
                                selectedCategory={selectedCategory}
                                setInformationMaxHeight={setInformationMaxHeight}
                                removePart={removePart}
                                pinned
                            />
                        ))
                    }
                    </tbody>
                </table>
                <div className={'compare-table-container'}>
                    <table className={'compare-table'}>
                        <thead>
                        <CompareViewTableHeaderRow
                            parts={unpinnedParts}
                            togglePinned={togglePinned}
                        />
                        </thead>
                        <tbody>
                        {
                            [null, ...(properties ?? [])].map(property => (
                                <CompareViewTableBodyRow
                                    key={`table-row-${property?.id ?? 'init'}`}
                                    property={property}
                                    parts={unpinnedParts}
                                    selectedCategory={selectedCategory}
                                    removeProperty={removeProperty}
                                    setInformationMaxHeight={setInformationMaxHeight}
                                    removePart={removePart}
                                />
                            ))
                        }
                        </tbody>
                    </table>
                </div>
            </div>
        </>
    );
}

const CompareViewTableHeaderRow = ({parts, togglePinned, pinned}) => {

    return (
        <>
            <tr>
                {
                    pinned &&
                    <td>
                        <div className={'compare-table-header'}/>
                    </td>
                }
                {
                    parts?.map(part => (
                        <td key={`header-${part.id}`}>
                            <div className={'compare-table-header'} onClick={() => togglePinned(part)}>
                                {
                                    part?.pinned
                                        ? <PinnedIcon/>
                                        : <PinIcon/>
                                }
                                <Tooltip title={part?.partNo ?? ''}>
                                    <p>
                                        {part?.partNo ?? ''}
                                    </p>
                                </Tooltip>
                            </div>
                        </td>
                    ))
                }
            </tr>

        </>
    );
}

const CompareViewTableBodyRow = ({
                                     property,
                                     parts,
                                     pinned,
                                     removeProperty,
                                     selectedCategory,
                                     setInformationMaxHeight,
                                     removePart,
                                 }) => {

    return (
        <tr>
            {
                // Image section
                property === null &&
                <>
                    {
                        pinned &&
                        <td>
                            <div className={'compare-view-part-info-container'}/>
                        </td>
                    }
                    {
                        parts?.map(part => (
                            <td key={`part-image-${part.id}`}>
                                <CompareViewPartInformationSection
                                    part={part}
                                    selectedCategory={selectedCategory}
                                    setMaxHeight={setInformationMaxHeight}
                                    removePart={removePart}
                                />
                            </td>
                        ))
                    }
                </>
            }
            {
                // Properties section
                property !== null &&
                <>
                    {
                        pinned &&
                        <td>
                            <div className={'compare-table-cell'}>
                                <IconButton
                                    className={'mr-2'}

                                    onPressed={() => removeProperty(property)}
                                    color={'paper.secondary'}
                                >
                                    <Close className='icon small'/>
                                </IconButton>
                                <Tooltip title={property?.title ?? ''} placement={'bottom-start'}>
                                    <p>
                                        {property?.title ?? ''}
                                    </p>
                                </Tooltip>
                            </div>
                        </td>
                    }
                    {
                        // Property values section
                        parts?.map((part, i) => {
                            const values = part?.properties?.filter(e => e.propertyId === property.id)?.map(e => e.value) ?? [];
                            const value = property.industryPropertyTypeId === ProductTechnicalPropertyTypeIds.color
                                ? values.map(e => Utils.formatTechnicalPropertyValueDisplay(property.industryPropertyTypeId, e))
                                : values.map(e => Utils.formatTechnicalPropertyValueDisplay(property.industryPropertyTypeId, e)).join(', ')
                            return (
                                <td key={`property-${property.id}-part-${part.id}`}>
                                    <div className={'compare-table-cell'}>
                                        {
                                            property.industryPropertyTypeId === ProductTechnicalPropertyTypeIds.color &&
                                            <Tooltip title={values.join(', ')}>
                                                <Box sx={{display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5}}>
                                                    {
                                                        value.map((colorValue, index) => (
                                                            <Box key={index} component={'span'}>
                                                                {colorValue}
                                                            </Box>
                                                        ))
                                                    }
                                                </Box>
                                            </Tooltip>
                                        }
                                        {
                                            property.industryPropertyTypeId !== ProductTechnicalPropertyTypeIds.color &&
                                            <Tooltip title={value}>
                                                <p>
                                                    {value}
                                                </p>
                                            </Tooltip>
                                        }
                                    </div>
                                </td>
                            );
                        })
                    }
                </>
            }
        </tr>
    );
}

const CompareViewPartInformationSection = ({part, selectedCategory, setMaxHeight, removePart: removePartProps}) => {
    const [activeIndex, setActiveIndex] = useState(0);
    const [removing, setRemoving] = useState(false);
    /**@type {React.MutableRefObject<HTMLDivElement>}*/
    const containerRef = useRef();
    const isMounted = useIsMounted();

    const images = useMemo(() => (!part?.images?.length ? [null] : part?.images), [part])
    // const priceNotAvailable = useMemo(() => part?.price <= 0, [part]);
    // const hasNewPrice = useMemo(() => part?.price < part?.originalPrice, [part]);

    /**
     * As soon as the component mounts:
     * - attaches an observer that determines the height of the content.
     */
    useEffect(() => {
        if (!containerRef.current)
            return;
        const observer = ObserversService.newResizeObserver(determineHeight);
        ObserversService.observeResizeObserver(observer, containerRef.current);
        determineHeight().then();
        return () => ObserversService.disconnectResizeObserver(observer);
    }, [containerRef, part?.id, selectedCategory?.id])

    /**
     * Calls the appropriate callback with the height of the content.
     */
    const determineHeight = async () => {
        await new Promise(r => setTimeout(r, 200));
        if (!isMounted())
            return;
        const height = containerRef.current?.getBoundingClientRect()?.height ?? 0;
        setMaxHeight(height);
    }

    /**
     * After each change in the slide, sets the [activeSlide] to the received number
     * @param index
     */
    const afterSlideChange = (index) => {
        setActiveIndex(index);
    }

    /**
     * Removes the given part from the list table parts.
     */
    const removePart = async () => {
        setRemoving(true);
        await removePartProps(part);
        setRemoving(false);
    }

    return (
        <>
            <div ref={containerRef} className={'compare-view-part-info-container'}>
                <div className={'compare-view-part-info'}>
                    <div className={'part-image'}>
                        <Slider
                            autoplay={false}
                            infinite={true}
                            initialSlide={0}
                            afterChange={afterSlideChange}
                            slidesToShow={1}
                        >
                            {
                                images.map((image) => (
                                    <div key={image?.id ?? 'placeholder'} className="img">
                                        <LazyImage
                                            src={image?.fileName}
                                            alt={image?.fileName ?? 'placeholder'}
                                            height={150}
                                            onContextMenu={(e) => e.preventDefault()}
                                            onDragStart={(e) => e.preventDefault()}
                                            className={'disable-downloading-image'}
                                        />
                                    </div>
                                ))
                            }
                        </Slider>
                        <div className={'custom-dots'}>
                            {
                                images.map((item, index) => (
                                    <div
                                        key={item?.id ?? 'placeholder'}
                                        className={classnames(
                                            'dot',
                                            {
                                                'active': index === activeIndex,
                                                'active-minus-one': (index === activeIndex + 1) || (index === activeIndex - 1),
                                                'active-minus-two': (index === activeIndex + 2) || (index === activeIndex - 2),
                                            }
                                        )}/>
                                ))
                            }
                        </div>
                        <Tooltip title={'Remove product'} placement={'top'} arrow>
                            <IconButton
                                color={'paper.error'}
                                onClick={removePart}
                                disabled={removing}
                                className={'remove p-0'}
                            >
                                <RemoveIcon className={'icon medium'}/>
                            </IconButton>
                        </Tooltip>
                        <Fade in={!removing}>
                            <Tooltip title={"View product information"} placement={'top'} arrow>
                                <IconButton
                                    color={'paper.secondary'}
                                    disabled={removing}
                                    className={'preview p-0'}
                                    component={Link}
                                    to={routeFunctions.lookup.single(part.partNo)}
                                    target={'_blank'}
                                >
                                    <EyeIcon/>
                                </IconButton>
                            </Tooltip>
                        </Fade>

                    </div>
                    <div className={'information'}>
                        <p className={'main-category'}>
                            {selectedCategory?.title ?? ''}
                        </p>
                        {
                            selectedCategory?.id !== part?.category?.id &&
                            <p className={'sub-category'}>
                                {part?.category?.title ?? ''}
                            </p>
                        }
                    </div>
                </div>
            </div>
        </>
    );
}

export default CompareViewTableSection;
